import { useCallback } from 'react'
import { Logo } from '@highlight/ui'
import { Calendar, Plug } from 'lucide-react'
import { useCalendars } from '@renderer/hooks/use-calendars'
import { useIntegrationConnections } from '@renderer/hooks/use-integration-connections'
import { ProviderIcon } from '@renderer/components/provider-icon'
import { useOpenSettingsTab } from '../../utils/use-open-settings-tab'
import { SidepanelBanner } from './sidepanel-banner'

export function SidepanelIntegrationsBanner({ className }: { className?: string }) {
  const { active, expired, isLoading } = useIntegrationConnections()
  const { expiredAccounts: expiredCalendars, isLoading: isLoadingCalendars } = useCalendars()
  const openSettingsTab = useOpenSettingsTab()

  // The two connection-health states route to Integrations rather than starting
  // OAuth here — with several providers possibly involved, the settings surface
  // owns picking among them.
  const handleOpen = useCallback(() => {
    openSettingsTab('integrations')
  }, [openSettingsTab])
  const handleOpenCalendars = useCallback(() => {
    openSettingsTab('calendars')
  }, [openSettingsTab])

  if (isLoading || isLoadingCalendars) return null

  // Calendars aren't in the toolset catalog behind `useIntegrationConnections`, so a dead calendar
  // would never show below. It goes first because the user stops getting meeting reminders.
  if (expiredCalendars.length > 0) {
    const only = expiredCalendars.length === 1 ? expiredCalendars[0] : null
    return (
      <SidepanelBanner
        className={className}
        icon={
          only ? (
            <Logo name={only.providerName === 'google' ? 'google-calendar' : 'outlook'} size={16} />
          ) : (
            <Calendar className="size-3.5 shrink-0" />
          )
        }
        message={only ? 'Calendar disconnected' : `${expiredCalendars.length} calendars disconnected`}
        action="Reconnect"
        onAction={handleOpenCalendars}
      />
    )
  }

  // Nothing connected yet — prompt to connect.
  if (active.length === 0) {
    return (
      <SidepanelBanner
        className={className}
        icon={<Plug className="size-3.5 shrink-0" />}
        message="Connect integrations to get the most out of Highlight"
        action="Connect"
        onAction={handleOpen}
      />
    )
  }

  // Something is connected but has expired. A single expired connection shows
  // its own icon; multiple fall back to the generic socket icon.
  if (expired.length > 0) {
    const only = expired.length === 1 ? expired[0] : null
    return (
      <SidepanelBanner
        className={className}
        icon={
          only ? (
            <div className="flex size-4 shrink-0 items-center justify-center">
              <ProviderIcon provider={only} size={16} />
            </div>
          ) : (
            <Plug className="size-3.5 shrink-0" />
          )
        }
        message={only ? `${only.name} disconnected` : `${expired.length} integrations disconnected`}
        action="Reconnect"
        onAction={handleOpen}
      />
    )
  }

  return null
}
